import { useApolloClient } from "react-apollo";
import gql from "graphql-tag";
import { useCallback } from "react";

import { runPromisesInSeries } from "../utils";

import { UPDATE_ORDER_MUTATION } from "../graphql/Orders/Orders.graphql";

import { useCurrentUser } from "../utils/hooks";

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const CREATE_INVOICE_MUTATION = gql`
  mutation CREATE_INVOICE_MUTATION($data: InvoiceCreateInput!) {
    createInvoice(data: $data) {
      RecId
    }
  }
`;
const UPDATE_INVOICE_MUTATION = gql`
  mutation UPDATE_INVOICE_MUTATION(
    $data: InvoiceUpdateInput!
    $where: InvoiceWhereUniqueInput!
  ) {
    updateInvoice(data: $data, where: $where) {
      RecId
    }
  }
`;
const FIND_FIRST_INVOICE_QUERY = gql`
  query FIND_FIRST_INVOICE_QUERY($where: InvoiceWhereInput) {
    Invoices(where: $where, first: 1) {
      RecId
    }
  }
`;

function useVoidInvoices() {
  const client = useApolloClient();
  const { data: CurrentUserData } = useCurrentUser();

  return useCallback(
    async selectedData => {
      const { data: BillingVerifiedSystemCodeData } = await client.query({
        query: ALL_SYSTEM_CODES_QUERY,
        variables: {
          where: {
            Category: "Order",
            CodeName: "OrderStatus",
            CodeId: "BV"
          }
        }
      });

      const billingVerifiedSystemCode =
        BillingVerifiedSystemCodeData?.SystemCodes?.[0];

      if (!billingVerifiedSystemCode) throw new Error();

      await runPromisesInSeries(
        selectedData.map(
          (
            { RecId, InvoiceNumber, InvoiceBatchNumber, InvoiceDate },
            index
          ) => async () => {
            const obj = {
              mutation: UPDATE_ORDER_MUTATION,
              variables: {
                data: {
                  StatusRecIdRef: billingVerifiedSystemCode.RecId,
                  InvoiceBatchNumber: null,
                  InvoiceNumber: null,
                  InvoiceDate: null
                },
                where: {
                  RecId
                }
              }
            };
            if (index === selectedData.length - 1) {
              obj.refetchQueries = ["ALL_ORDERS_VIEW_BY_PAGE_QUERY"];
            }

            const { data: MatchingInvoiceData } = await client.query({
              query: FIND_FIRST_INVOICE_QUERY,
              variables: {
                where: {
                  OrderRecIdRef: RecId,
                  InvoiceNumber
                }
              }
            });
            console.log(MatchingInvoiceData);
            const matchingInvoice = MatchingInvoiceData?.Invoices?.[0];

            if (matchingInvoice) {
              await client.mutate({
                mutation: UPDATE_INVOICE_MUTATION,
                variables: {
                  where: {
                    RecId: matchingInvoice.RecId
                  },
                  data: {
                    OrderRecIdRef: RecId,
                    InvoiceNumber,
                    InvoiceBatchNumber,
                    InvoiceDateTime: InvoiceDate,
                    Status: "VOID",
                    InvoiceVoidDate: new Date().setUTCHours(13),
                    InvoiceVoidBy: CurrentUserData?.UserId
                  }
                }
              });
            } else {
              await client.mutate({
                mutation: CREATE_INVOICE_MUTATION,
                variables: {
                  data: {
                    OrderRecIdRef: RecId,
                    InvoiceNumber,
                    InvoiceBatchNumber,
                    InvoiceDateTime: InvoiceDate,
                    Status: "VOID",
                    InvoiceVoidDate: new Date().setUTCHours(13),
                    InvoiceVoidBy: CurrentUserData?.UserId
                  }
                }
              });
            }

            return client.mutate(obj);
          }
        )
      );
    },
    [CurrentUserData.UserId, client]
  );
}

export default useVoidInvoices;
